import { isQueryToolbarAction } from './resetListPage'

/**
 * 是否为导入/导出类按钮。创建于 2026-08-28
 */
export function isImportExportToolbarAction(item) {
  if (!item) return false
  const name = String(item.name || '')
  const func = String(item.Func || '').toLowerCase()
  if (/导入|导出|下载|上传|同步/i.test(name)) return true
  if (/export|import|download|upload|sync/i.test(func)) return true
  return false
}

/**
 * 是否为批量操作按钮。创建于 2026-08-28
 */
export function isBatchToolbarAction(item) {
  if (!item) return false
  const name = String(item.name || '')
  const func = String(item.Func || '').toLowerCase()
  if (/批量|批处理/.test(name)) return true
  if (/^batch|batchremove|batchdel|batchupdate|batchchange/i.test(func)) return true
  return false
}

/**
 * 是否为常用主操作（查询/增删改）。创建于 2026-08-28
 */
export function isPrimaryToolbarAction(item) {
  if (!item || item.IsHide) return false
  if (isQueryToolbarAction(item)) return true
  const name = String(item.name || '')
  const func = String(item.Func || '').toLowerCase()
  if (/^新增|^添加|^创建/i.test(name) || /^add|^create/i.test(func)) return true
  if (/^编辑|^修改/i.test(name) || /^edit|^update/i.test(func)) return true
  if ((/^删除|^移除/i.test(name) || /^del|^remove/i.test(func)) && !isBatchToolbarAction(item)) return true
  return false
}

/**
 * 将工具栏按钮拆成主操作 + 扩展分组。创建于 2026-08-28
 */
export function groupToolbarButtons(buttonList, options = {}) {
  const threshold = options.threshold == null ? 6 : options.threshold
  const visible = (buttonList || []).filter((item) => !item.IsHide)
  const useGrouped = visible.length > threshold

  const primaryButtons = []
  const importExportButtons = []
  const batchButtons = []
  const moreButtons = []

  if (!useGrouped) {
    return {
      useGrouped: false,
      inlineButtons: visible,
      primaryButtons: visible,
      importExportButtons: [],
      batchButtons: [],
      moreButtons: [],
      overflowSections: [],
    }
  }

  visible.forEach((item) => {
    if (isPrimaryToolbarAction(item)) {
      primaryButtons.push(item)
      return
    }
    if (isImportExportToolbarAction(item)) {
      importExportButtons.push(item)
      return
    }
    if (isBatchToolbarAction(item)) {
      batchButtons.push(item)
      return
    }
    moreButtons.push(item)
  })

  const overflowSections = []
  if (importExportButtons.length) {
    overflowSections.push({ key: 'importExport', title: '导入导出', buttons: importExportButtons })
  }
  if (batchButtons.length) {
    overflowSections.push({ key: 'batch', title: '批量操作', buttons: batchButtons })
  }
  if (moreButtons.length) {
    overflowSections.push({ key: 'more', title: '其他工具', buttons: moreButtons })
  }

  return {
    useGrouped: true,
    inlineButtons: visible,
    primaryButtons,
    importExportButtons,
    batchButtons,
    moreButtons,
    overflowSections,
  }
}

/**
 * 是否为新增类按钮。创建于 2026-08-28
 */
export function isAddToolbarAction(item) {
  if (!item) return false
  const name = String(item.name || '')
  const func = String(item.Func || '').toLowerCase()
  if (/^新增|^添加|^创建/i.test(name) || /^add|^create/i.test(func)) return true
  return false
}

/**
 * 是否为编辑类按钮。创建于 2026-08-28
 */
export function isEditToolbarAction(item) {
  if (!item) return false
  const name = String(item.name || '')
  const func = String(item.Func || '').toLowerCase()
  if (/^编辑|^修改/i.test(name) || /^edit|^update/i.test(func)) return true
  return false
}

const TOOLBAR_TONE_RULES = [
  { tone: 'violet', pattern: /设置|配置|流程|规则|权限/ },
  { tone: 'cyan', pattern: /记录|日志|历史|详情|明细/ },
  { tone: 'indigo', pattern: /统计|报表|分析|图表/ }
]

/**
 * 按名称匹配扩展色调（设置紫 / 记录青 / 统计靛），未匹配返回空串。创建于 2026-10-12
 */
export function resolveToolbarBtnTone(item) {
  if (!item) return ''
  const name = String(item.name || '')
  const rule = TOOLBAR_TONE_RULES.find(r => r.pattern.test(name))
  return rule ? rule.tone : ''
}

/**
 * 工具栏按钮统一外观（全站唯一入口）。最后修改：2026-10-12
 * 查询蓝 / 新增绿 / 编辑灰 / 导入导出橙 / 删除红 / 设置紫 / 记录青 / 统计靛 / 其他灰
 */
export function resolveToolbarBtnProps(item) {
  if (isQueryToolbarAction(item)) {
    return { type: 'primary', plain: false }
  }
  if (isDangerToolbarAction(item)) {
    return { type: 'danger', plain: false }
  }
  if (isImportExportToolbarAction(item)) {
    return { type: 'warning', plain: false }
  }
  if (isAddToolbarAction(item)) {
    return { type: 'success', plain: false }
  }
  if (isEditToolbarAction(item)) {
    return { type: 'info', plain: false }
  }
  const tone = resolveToolbarBtnTone(item)
  if (tone) {
    return { type: 'info', plain: false, tone }
  }
  return { type: 'info', plain: false }
}

/**
 * @deprecated 请使用 resolveToolbarBtnProps
 */
export function resolvePrimaryToolbarBtnProps(item) {
  return resolveToolbarBtnProps(item)
}

/**
 * @deprecated 请使用 resolveToolbarBtnProps
 */
export function resolveToolbarBtnType(item) {
  return resolveToolbarBtnProps(item).type
}

/**
 * 工具栏按钮默认图标（主按钮与更多菜单共用）。创建于 2026-08-28
 */
export function resolveToolbarBtnIcon(item) {
  if (item && item.icon) return item.icon

  if (isQueryToolbarAction(item)) return 'el-icon-search'
  if (isDangerToolbarAction(item)) return 'el-icon-delete'
  if (isImportExportToolbarAction(item)) {
    const name = String(item.name || '')
    const func = String(item.Func || '').toLowerCase()
    if (/导出|下载/.test(name) || /export|download/i.test(func)) return 'el-icon-download'
    if (/同步/.test(name) || /sync/i.test(func)) return 'el-icon-refresh'
    return 'el-icon-upload2'
  }
  if (isAddToolbarAction(item)) return 'el-icon-plus'
  if (isEditToolbarAction(item)) return 'el-icon-edit'

  const name = String(item.name || '')
  const func = String(item.Func || '').toLowerCase()
  if (/刷新|重置/.test(name) || /refresh|reset/i.test(func)) return 'el-icon-refresh-left'
  if (/批量/.test(name) || /batch/i.test(func)) return 'el-icon-s-operation'
  if (/二维码|qr/i.test(name) || /qr/i.test(func)) return 'el-icon-full-screen'
  if (/压缩|头像/.test(name) || /compress|avatar|photo/i.test(func)) return 'el-icon-picture-outline'
  if (/迁移|migrate/i.test(name) || /migrate/i.test(func)) return 'el-icon-upload'
  if (/打印|print/i.test(name) || /print/i.test(func)) return 'el-icon-printer'
  if (/保存|save/i.test(name) || /save/i.test(func)) return 'el-icon-check'

  return 'el-icon-more-outline'
}

/**
 * 扩展菜单项默认图标。创建于 2026-08-28
 */
export function resolveToolbarOverflowIcon(item) {
  return resolveToolbarBtnIcon(item)
}

/**
 * 扩展菜单分组图标。创建于 2026-08-28
 */
export function resolveToolbarOverflowSectionIcon(sectionKey) {
  const map = {
    importExport: 'el-icon-files',
    batch: 'el-icon-s-operation',
    more: 'el-icon-set-up',
  }
  return map[sectionKey] || 'el-icon-menu'
}

/**
 * 扩展菜单项是否危险操作。创建于 2026-08-28
 */
export function isDangerToolbarAction(item) {
  const name = String(item.name || '')
  const func = String(item.Func || '').toLowerCase()
  if (/刷新|导出|导入|同步|下载|上传/.test(name)) return false
  if (/refresh|export|import|sync|download|upload/i.test(func)) return false
  if (/删除|移除|清空/.test(name)) return true
  if (/del|remove|clear|batchdel/i.test(func)) return true
  return false
}
